// Third-party libraries
import React, { Component } from "react";
import { scaleLinear, scaleSymlog } from "d3-scale";
import { max, min } from "d3-array";
import { select, selectAll } from "d3-selection";
import { axisLeft } from "d3-axis";
import { line, curveMonotoneX } from "d3-shape";

// Project dependencies
import { payoutToOdds } from "../utilities";
import { debounce } from "../utilities";
import { Option, OptionData } from "../shared/interfaces";
import variables from "../styleVariables";

class PayoutHistory extends Component {
  // Some monkey business to satisfy Typescript
  props: any;
  context: any;
  setState: any;
  forceUpdate: any;
  state: any;
  refs: any;
  constructor(props: any) {
    super(props);
    this.payoutHistory_el = React.createRef();
  }

  componentDidMount() {
    const delayedMakeChart = debounce(() => {
      if (this.payoutHistory_el.current) {
        this.drawChart();
      }
    }, 100);

    if (this.payoutHistory_el.current) {
      window.addEventListener("resize", delayedMakeChart);
      this.drawChart();
    }
  }

  componentDidUpdate(prevProps: any) {
    if (
      JSON.stringify(this.props.options) !== JSON.stringify(prevProps.options)
    ) {
      this.drawChart();
    }
  }

  drawChart() {
    let options: Array<OptionData> = this.props.options;
    if (!options) {
      return;
    }
    const outerWidth = this.payoutHistory_el.current.getBoundingClientRect()
      .width;
    const outerHeight = this.payoutHistory_el.current.getBoundingClientRect()
      .height;
    options.map((option: OptionData) => {
      if (
        typeof option.history === "string" ||
        option.history instanceof String
      ) {
        option.history = option.history
          .split(",")
          .filter(payout => payout.length > 0)
          .map(payout => parseFloat(payout)); // Accounts for leading comma and empty values

        option.history.push(option.payout);
      }
      return option;
    });
    selectAll("svg#payout-history > *").remove();
    var margin = { top: 15, right: 0, bottom: 15, left: 80 },
      width = outerWidth - margin.left - margin.right,
      height = outerHeight - margin.top - margin.bottom;

    const maxPayout = max(options.map(option => max(option.history)));
    const minPayout = min(options.map(option => min(option.history)));
    const currentMinPayout = min(
      options.map(option => option.history[option.history.length - 1])
    );
    const currentMaxPayout = max(
      options.map(option => option.history[option.history.length - 1])
    );

    const minHistoryLength = min(options.map(option => option.history.length));

    // Standardize the length of histories
    options.map(option => {
      option.history = option.history.slice(-minHistoryLength);
      return option;
    });

    var x = scaleLinear()
      .domain([0, minHistoryLength - 1])
      .range([0, width]);
    var y = scaleSymlog()
      .domain([minPayout, maxPayout])
      .range([0, height]);

    var svg = select("svg#payout-history")
      .append("g")
      .attr("transform", "translate(" + margin.left + "," + margin.top + ")");

    // // Y axis
    svg
      .append("g")
      .attr("class", "axis")
      .call(
        axisLeft(y)
          .tickValues(
            [1.1, 3, 6, 11, 21, 51, 101, 201, 501, 1001].filter(
              payout => payout < maxPayout && payout > minPayout
            )
          )
          .tickFormat(function(d) {
            return `${payoutToOdds(d)[0]}/${payoutToOdds(d)[1]}`;
          })
      );

    // X axis
    svg
      .append("g")
      .attr("transform", `translate(0,${height})`) //"translate(0," + height + ")")
      .attr("class", "axis");

    // text labels for the y axis
    svg
      .append("text")
      .attr("y", y(minPayout) + 2)
      .attr("x", 0 - margin.left)
      .attr("class", "axis-label--small")
      .text("Shorter");
    svg
      .append("text")
      .attr("y", y(maxPayout) + 2)
      .attr("x", 0 - margin.left)
      .attr("class", "axis-label--small")
      .text("Longer");

    // text labels for the x axis
    svg
      .append("text")
      .attr("y", height + 15)
      .attr("x", 3)
      .attr("class", "axis-label--small")
      .text("\u2190 First bets");
    svg
      .append("text")
      .attr("y", height + 15)
      .attr("x", width)
      .attr("text-anchor", "end")
      .attr("class", "axis-label--small")
      .text("Latest bets \u2192");

    const drawLine = (
      payout_history, // An array of payouts
      hovered = false, // Is this line being hovered over?
      option: string | null = null, // Is there a name to display in the tooltip?
      winner: Option | null = null // If this prop has been scored, this is a winner to display
    ) => {
      var payoutLine = line()
        .x((d, i) => x(i))
        .y(d => y(d))
        .curve(curveMonotoneX); // apply smoothing to the line

      const currentPayout = payout_history[payout_history.length - 1];

      console.log(option, currentPayout)

      const getStroke = () => {
        if (
          currentPayout === currentMinPayout ||
          (winner && winner.name === option)
        ) {
          return variables.colorPrimary;
        }
        if (currentPayout === currentMaxPayout) {
          return variables.colorCancel;
        } else {
          return variables.colorSuccess;
        }
      };
      if (option) {
        select(".history__tooltip--payout")
          .style(
            "top",
            y(payout_history[payout_history.length - 1]) - 20 + "px"
          )
          .text(
            `${option} (${payoutToOdds(currentPayout)[0]}/${
              payoutToOdds(currentPayout)[1]
            })`
          );
      }
      svg
        .append("path")
        .data([payout_history])
        .attr("class", `line ${hovered === false ? "line--neutral" : ""}`)
        .attr("stroke-width", "2")
        .attr("stroke", () => {
          if (winner && winner.name === option) {
            return variables.colorPrimary;
          }
          if (winner && hovered) {
            return variables.colorSecondary;
          }
          if (hovered) {
            return getStroke();
          } else {
            return variables.colorRule;
          }
        })
        .attr("d", payoutLine)
        .classed("line--hover", hovered)
        .on("mouseover", d => {
          // Draw a line on top
          if (!hovered || winner) {
            selectAll("svg#payout-history .line--hover").remove();
            selectAll("svg#payout-history .history__endpoint").remove();
            drawLine(d, true, option, winner);
            svg
              .append("circle")
              .attr("class", "history__endpoint")
              .attr("r", 3)
              .attr("cx", x(d.length - 1))
              .attr("cy", y(d[d.length - 1]))
              .style("fill", () => {
                if (winner && winner.name === option) {
                  return variables.colorPrimary;
                }
                if (winner) {
                  return variables.colorSecondary;
                } else {
                  return getStroke();
                }
              });
            select(".history__tooltip--payout")
              .style("top", y(d[d.length - 1]) - 20 + "px")
              .text(
                `${option} (${payoutToOdds(currentPayout)[0]}/${
                  payoutToOdds(currentPayout)[1]
                })`
              );
          }
        });
    };

    console.log({options})
    options.map(option =>
      drawLine(option.history, false, option.option.name, this.props.winner)
    );

    // Draw the initial line, either the current favorite or the winner
    let starter: Option = this.props.winner
      ? options.find(option => option.option.name === this.props.winner.name)
      : this.props.favorite;
    
    if (starter) {
      drawLine(
        starter.history,
        true, // hovered
        starter.option.name,
        this.props.winner
      );
      svg
        .append("circle")
        .attr("class", "history__endpoint")
        .attr("r", 3)
        .attr("cx", x(starter.history.length - 1))
        .attr("cy", y(starter.history[starter.history.length - 1]))
        .style("fill", variables.colorPrimary);
    }
  }

  render() {
    return (
      <div className="history__outer">
        <div className="history__tooltip history__tooltip--payout" />
        <svg
          id="payout-history"
          className={"history chart"}
          ref={this.payoutHistory_el}
          width="100%"
          height={
            this.props.height || this.props.options.length > 7
              ? this.props.options.length * 20
              : 150
          }
        />
      </div>
    );
  }
}

export default PayoutHistory;
